import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Login from './pages/Login';
import AdminDashboard from './pages/AdminDashboard';
import Sessions from './pages/Sessions';
import HealingRequests from './pages/HealingRequests';
import CourseCompletions from './pages/CourseCompletions';
import Users from './pages/Users';
import StudentDashboard from './pages/StudentDashboard';
import PatientDashboard from './pages/PatientDashboard';
import HealerDashboard from './pages/HealerDashboard';
import GeneralDashboard from './pages/GeneralDashboard';
import WPCallback from './pages/WPCallback';
import Announcements from './pages/Announcements';
import Finance from './pages/Finance';
import SessionPayouts from './pages/SessionPayouts';
import BillingPage from './pages/BillingPage';
import Impersonate from './pages/Impersonate';
import PractitionerActivities from './pages/PractitionerActivities';
import SuperAdminDashboard from './pages/SuperAdminDashboard';
import OwnerDashboard from './pages/OwnerDashboard';
import Privacy from './pages/Privacy';
import SoloBookingPage    from './pages/SoloBookingPage';
import CentreBookingPage  from './pages/CentreBookingPage';
import SoloRegisterPage   from './pages/SoloRegisterPage';
import SoloDashboard from './pages/SoloDashboard';
import SoloHealerDashboard from './pages/SoloHealerDashboard';
import SetPasswordPage        from './pages/SetPasswordPage';
import SoloPatientLoginPage   from './pages/SoloPatientLoginPage';
import SoloPatientDashboard   from './pages/SoloPatientDashboard';
import PatientOnboarding from './pages/PatientOnboarding';
import RegisterPatient from './pages/RegisterPatient';
import RegisterHealer  from './pages/RegisterHealer';
import RegisterCentre   from './pages/RegisterCentre';
import CentreSetupPage  from './pages/CentreSetupPage';
import RegisterSuccess  from './pages/RegisterSuccess';
import Referrals         from './pages/Referrals';
import HealerSchedules  from './pages/HealerSchedules';
import PromoManager     from './pages/PromoManager';
import PackageManager   from './pages/PackageManager';
import PackageSales         from './pages/PackageSales';
import PushCampaign         from './pages/PushCampaign';
import AffinityInsightsPage from './pages/AffinityInsightsPage';
import FamilyManager        from './pages/FamilyManager';
import PaymentPending       from './pages/PaymentPending';
import BillingCheckout      from './pages/BillingCheckout';
import { BillingProvider }  from './context/BillingContext';
import BillingTokenGate     from './pages/billing/BillingTokenGate';
import BillingDashboard     from './pages/billing/BillingDashboard';
import PatientProfile       from './pages/PatientProfile';

function App() {
  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID || ''}>
    <BillingProvider>
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register"         element={<RegisterPatient />} />
          <Route path="/register/healer"  element={<RegisterHealer />} />
          <Route path="/register/success" element={<RegisterSuccess />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/book/solo/:slug" element={<SoloBookingPage />} />
          <Route path="/book/:slug"      element={<CentreBookingPage />} />
          <Route path="/solo/register"     element={<SoloRegisterPage />} />
          <Route path="/register/centre"       element={<RegisterCentre />} />
          <Route path="/register/centre/setup" element={<CentreSetupPage />} />
          <Route path="/set-password"             element={<SetPasswordPage />} />
          <Route path="/payment-pending"          element={<PaymentPending />} />
          <Route path="/billing/checkout"         element={<BillingCheckout />} />
          <Route path="/solo-patient/login"      element={<SoloPatientLoginPage />} />
          <Route path="/solo-patient/dashboard"  element={<SoloPatientDashboard />} />
          <Route path="/solo/dashboard" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <SoloDashboard />
            </ProtectedRoute>
          } />
          <Route path="/solo/healer" element={
            <ProtectedRoute allowedRoles={['healer']}>
              <SoloHealerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/solo/billing" element={
            <ProtectedRoute allowedRoles={['admin']}>
              <BillingPage />
            </ProtectedRoute>
          } />
          <Route path="/wp-callback" element={<WPCallback />} />
          <Route path="/dashboard" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <AdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/sessions" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <Sessions />
            </ProtectedRoute>
          } />
          <Route path="/healing-requests" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <HealingRequests />
            </ProtectedRoute>
          } />
          <Route path="/course-completions" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <CourseCompletions />
            </ProtectedRoute>
          } />
          <Route path="/practitioner-activities" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <PractitionerActivities />
            </ProtectedRoute>
          } />
          <Route path="/users" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <Users />
            </ProtectedRoute>
          } />
          <Route path="/finance" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <Finance />
            </ProtectedRoute>
          } />
          <Route path="/session-payouts" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <SessionPayouts />
            </ProtectedRoute>
          } />
          <Route path="/billing" element={
            <BillingTokenGate>
              <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
                <BillingPage />
              </ProtectedRoute>
            </BillingTokenGate>
          } />
          <Route path="/billing/dashboard" element={<BillingDashboard />} />
          <Route path="/announcements" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <Announcements />
            </ProtectedRoute>
          } />
          <Route path="/patient-onboarding" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <PatientOnboarding />
            </ProtectedRoute>
          } />
          <Route path="/schedules" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin', 'owner']}>
              <HealerSchedules />
            </ProtectedRoute>
          } />
          <Route path="/referrals" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin', 'owner', 'healer']}>
              <Referrals />
            </ProtectedRoute>
          } />
          <Route path="/promos" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin', 'owner']}>
              <PromoManager />
            </ProtectedRoute>
          } />
          <Route path="/packages" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin', 'owner']}>
              <PackageManager />
            </ProtectedRoute>
          } />
          <Route path="/package-sales" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin', 'owner']}>
              <PackageSales />
            </ProtectedRoute>
          } />
          <Route path="/push-campaign" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin', 'owner']}>
              <PushCampaign />
            </ProtectedRoute>
          } />
          <Route path="/affinity-insights" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <AffinityInsightsPage />
            </ProtectedRoute>
          } />
          <Route path="/family" element={
            <ProtectedRoute allowedRoles={['patient']}>
              <FamilyManager />
            </ProtectedRoute>
          } />
          <Route path="/student" element={
            <ProtectedRoute allowedRoles={['student', 'admin', 'centreAdmin']}>
              <StudentDashboard />
            </ProtectedRoute>
          } />
          <Route path="/patient/profile" element={
            <ProtectedRoute allowedRoles={['patient']}>
              <PatientProfile />
            </ProtectedRoute>
          } />
          <Route path="/patient" element={
            <ProtectedRoute allowedRoles={['patient', 'admin', 'centreAdmin']}>
              <PatientDashboard />
            </ProtectedRoute>
          } />
          <Route path="/healer" element={
            <ProtectedRoute allowedRoles={['healer', 'admin', 'centreAdmin']}>
              <HealerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/general" element={
            <ProtectedRoute allowedRoles={['general', 'admin', 'centreAdmin']}>
              <GeneralDashboard />
            </ProtectedRoute>
          } />
          <Route path="/impersonate" element={
            <ProtectedRoute allowedRoles={['admin', 'centreAdmin']}>
              <Impersonate />
            </ProtectedRoute>
          } />
          <Route path="/superadmin" element={
            <ProtectedRoute allowedRoles={['superadmin']}>
              <SuperAdminDashboard />
            </ProtectedRoute>
          } />
          <Route path="/owner" element={
            <ProtectedRoute allowedRoles={['owner']}>
              <OwnerDashboard />
            </ProtectedRoute>
          } />
          <Route path="/owner/billing" element={
            <ProtectedRoute allowedRoles={['owner']}>
              <BillingPage />
            </ProtectedRoute>
          } />
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
    </BillingProvider>
    </GoogleOAuthProvider>
  );
}

export default App;
// 2026-05-04